import { useCallback, useMemo, useEffect, useRef } from 'react';
import {
  OnboardingProTask,
  OnboardingStatus,
  TaskStatus,
} from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/onboarding.pb';
import { ProductTier } from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/product.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import dayjs from 'dayjs';
import { useGetUserOnboardingStatusQuery, useUpdateOnboardingStatusMutation } from '@frontend/api-call-intel';
import { useHasCallIntelAclAccess } from '@frontend/call-intel-core';
import { useAppScopeStore } from '@frontend/scope';
import { usePreference } from '@frontend/user-preferences';
import { CI_PRO_CHECKLIST_POLICY } from '../constant';
import { buildCIProOnboardingFlow } from '../flow-descriptor';
import { CI_PRO_ONBOARDING_EVENTS, trackOnboardingEvent } from '../tracking';
import { getLatestCompletedAt, getCurrentTimestamp, hasDaysElapsed } from '../utils/onboarding-lifecycle';

interface ProTaskProgress {
  proTask: OnboardingProTask;
  status: TaskStatus;
  completedAt?: string;
}

type PendingTaskCompletionMetadata = {
  taskId: string;
  taskLabel?: string;
  completedEvent?: string;
  isAllCompletedAfterThisTask: boolean;
};

type UseProOnboardingProgressArgs = {
  enabled?: boolean;
  trackLifecycle?: boolean;
};

export function useProOnboardingProgress({
  enabled = true,
  trackLifecycle = false,
}: UseProOnboardingProgressArgs = {}) {
  const [lifecycle, setLifecycle] = usePreference('CIProOnboardingLifecycle');
  const expiryTrackedRef = useRef(false);
  const pendingTaskCompletionMetadataRef = useRef(new Map<OnboardingProTask, PendingTaskCompletionMetadata>());

  const { selectedLocationIds: appScopeLocationIds } = useAppScopeStore();

  const isPermanentlyHidden = !!lifecycle.hiddenAt;

  const {
    data: onboardingData,
    isLoading: queryLoading,
    isError: hasOnboardingStatusError,
  } = useGetUserOnboardingStatusQuery({
    request: { productTier: ProductTier.PRODUCT_TIER_PRO, locationIds: appScopeLocationIds },
    options: {
      refetchOnMount: false,
      enabled: enabled && !isPermanentlyHidden && !!appScopeLocationIds.length,
      retry: false,
    },
  });

  const isLoading = enabled && !isPermanentlyHidden && !hasOnboardingStatusError ? queryLoading : false;

  const canAssignUserRoles = useHasCallIntelAclAccess([Permission.CALL_INTELLIGENCE_READ, Permission.USER_WRITE], {
    allowDemoOverride: false,
    requireAllPermissions: true,
  });

  const canEditAiOutput = useHasCallIntelAclAccess(
    [Permission.CALL_INTELLIGENCE_EDIT_AI_OUTPUT, Permission.USER_WRITE],
    {
      allowDemoOverride: false,
      requireAllPermissions: true,
    }
  );

  const flow = useMemo(
    () => buildCIProOnboardingFlow({ canAssignUserRoles, canEditAiOutput }),
    [canAssignUserRoles, canEditAiOutput]
  );

  const onboardingStatus = onboardingData?.status;

  const tasks = useMemo(() => (onboardingData?.tasks ?? []) as ProTaskProgress[], [onboardingData?.tasks]);

  const completedGroups = useMemo(() => {
    if (onboardingStatus === OnboardingStatus.ONBOARDING_STATUS_COMPLETED) {
      return flow.tasks.map((task) => task.id as OnboardingProTask);
    }

    return tasks.filter((task) => task.status === TaskStatus.TASK_STATUS_COMPLETED).map((task) => task.proTask);
  }, [onboardingStatus, tasks, flow.tasks]);

  const isAllCompleted = useMemo(() => {
    if (flow.tasks.length === 0) return false;

    return flow.tasks.every((task) => completedGroups.includes(task.id as OnboardingProTask));
  }, [flow.tasks, completedGroups]);

  const latestTaskCompletedAtFromApi = useMemo(
    () =>
      getLatestCompletedAt(
        tasks.filter((task) => task.status === TaskStatus.TASK_STATUS_COMPLETED).map((task) => task.completedAt)
      ),
    [tasks]
  );

  const finalCompletedAtFromApi = latestTaskCompletedAtFromApi ?? lifecycle.lastTaskCompletedAt;

  const { mutate: updateStatus } = useUpdateOnboardingStatusMutation({
    options: {
      onSuccess: (_response, request) => {
        const proTask = request.task && 'proTask' in request.task ? request.task.proTask : undefined;
        const metadata = proTask ? pendingTaskCompletionMetadataRef.current.get(proTask) : undefined;

        if (metadata?.completedEvent) {
          trackOnboardingEvent(metadata.completedEvent, {
            taskId: metadata.taskId,
            taskLabel: metadata.taskLabel ?? null,
          });
        }

        if (metadata?.isAllCompletedAfterThisTask) {
          trackOnboardingEvent(CI_PRO_ONBOARDING_EVENTS.checklistCompleted);
        }

        if (proTask) {
          pendingTaskCompletionMetadataRef.current.delete(proTask);
        }
      },

      onError: (_error, request) => {
        const proTask = request.task && 'proTask' in request.task ? request.task.proTask : undefined;

        if (proTask) {
          pendingTaskCompletionMetadataRef.current.delete(proTask);
        }
      },
    },
  });

  /**
   * Sync PRO onboarding lifecycle timestamps.
   *
   * Writes:
   *
   * 1. firstSeenAt
   *    - set once when checklist is actually eligible/registered
   *    - used as 30-day expiry anchor if no task has been completed
   *
   * 2. lastTaskCompletedAt
   *    - synced from BE task completedAt
   *    - resets the 30-day incomplete expiry timer
   *
   * 3. completedAt
   *    - set when all checklist tasks are completed
   *    - starts the 14-day completed retention window
   */
  useEffect(() => {
    if (!trackLifecycle) return;
    if (isLoading || isPermanentlyHidden || hasOnboardingStatusError || !onboardingData) return;

    const nextLifecycle = { ...lifecycle };
    let changed = false;

    if (!nextLifecycle.firstSeenAt) {
      nextLifecycle.firstSeenAt = getCurrentTimestamp();
      changed = true;
    }

    const hasNewerTaskCompletedAt =
      latestTaskCompletedAtFromApi &&
      (!nextLifecycle.lastTaskCompletedAt ||
        dayjs(latestTaskCompletedAtFromApi).isAfter(dayjs(nextLifecycle.lastTaskCompletedAt)));

    if (hasNewerTaskCompletedAt) {
      nextLifecycle.lastTaskCompletedAt = latestTaskCompletedAtFromApi;
      changed = true;
    }

    if (isAllCompleted && !nextLifecycle.completedAt && finalCompletedAtFromApi) {
      nextLifecycle.completedAt = finalCompletedAtFromApi;
      changed = true;
    }

    if (changed) {
      setLifecycle(nextLifecycle);
    }
  }, [
    trackLifecycle,
    isLoading,
    isPermanentlyHidden,
    onboardingData,
    lifecycle,
    latestTaskCompletedAtFromApi,
    finalCompletedAtFromApi,
    isAllCompleted,
    setLifecycle,
    hasOnboardingStatusError,
  ]);

  const shouldExpireCompletedChecklist =
    isAllCompleted && hasDaysElapsed(lifecycle.completedAt, CI_PRO_CHECKLIST_POLICY.completedRetentionDays);

  const incompleteExpiryAnchor = lifecycle.lastTaskCompletedAt ?? lifecycle.firstSeenAt;

  const shouldExpireIncompleteChecklist =
    !isAllCompleted && hasDaysElapsed(incompleteExpiryAnchor, CI_PRO_CHECKLIST_POLICY.incompleteExpiryDays);

  useEffect(() => {
    if (!trackLifecycle) return;
    if (isLoading || isPermanentlyHidden || hasOnboardingStatusError) return;
    if (!shouldExpireCompletedChecklist && !shouldExpireIncompleteChecklist) return;
    if (expiryTrackedRef.current) return;

    expiryTrackedRef.current = true;

    trackOnboardingEvent(
      shouldExpireCompletedChecklist
        ? CI_PRO_ONBOARDING_EVENTS.checklistExpiredComplete
        : CI_PRO_ONBOARDING_EVENTS.checklistExpiredIncomplete
    );

    setLifecycle({
      ...lifecycle,
      hiddenAt: getCurrentTimestamp(),
      hiddenReason: shouldExpireCompletedChecklist ? 'completed-expired' : 'incomplete-expired',
    });
  }, [
    trackLifecycle,
    isLoading,
    isPermanentlyHidden,
    shouldExpireCompletedChecklist,
    shouldExpireIncompleteChecklist,
    lifecycle,
    setLifecycle,
    hasOnboardingStatusError,
  ]);

  const completeTask = useCallback(
    (groupId: string) => {
      const proTask = groupId as OnboardingProTask;

      if (hasOnboardingStatusError) return;

      if (completedGroups.includes(proTask)) return;

      const isAllCompletedAfterThisTask = flow.tasks.every(
        (task) => task.id === groupId || completedGroups.includes(task.id as OnboardingProTask)
      );

      const nextStatus = isAllCompletedAfterThisTask
        ? OnboardingStatus.ONBOARDING_STATUS_COMPLETED
        : OnboardingStatus.ONBOARDING_STATUS_STARTED;

      const completedTask = flow.tasks.find((task) => task.id === groupId);

      pendingTaskCompletionMetadataRef.current.set(proTask, {
        taskId: groupId,
        taskLabel: completedTask?.label,
        completedEvent: completedTask?.tracking?.completedEvent,
        isAllCompletedAfterThisTask,
      });

      updateStatus({
        productTier: ProductTier.PRODUCT_TIER_PRO,
        status: nextStatus,
        task: {
          proTask,
          status: TaskStatus.TASK_STATUS_COMPLETED,
        },
      });
    },
    [completedGroups, flow.tasks, hasOnboardingStatusError, updateStatus]
  );

  return {
    completedGroups,
    onboardingStatus,
    isLoading,
    hasOnboardingStatusError,
    completeTask,
    flow,
    isPermanentlyHidden,
    shouldExpireIncompleteChecklist,
    shouldExpireCompletedChecklist,
    incompleteExpiryAnchor,
  };
}
